// DynamicDrawer.tsx
import { Drawer, Button, CopyButton, Group, Tooltip } from '@mantine/core';
import { IconCopy, IconExternalLink, IconCheck } from '@tabler/icons-react';
import PostDrawerContent from './PostDrawerContent';
import EventDrawerContent from './EventDrawerContent';
import ArtistDrawerContent from './ArtistDrawerContent';

export type DrawerContentType = 'post' | 'event' | 'artist';

interface DynamicDrawerProps {
  opened: boolean;
  type: DrawerContentType;
  data: any; // Or use a union type: Post | Event | Artist
  onClose: () => void;
}

export default function DynamicDrawer({ opened, type, data, onClose }: DynamicDrawerProps) {
  let content = null;
  let pageUrl = '';
  let entityName = '';
  let entityTypeName = '';

  // Determine content, URL and entity type based on drawer type
  switch (type) {
    case 'post':
      content = <PostDrawerContent post={data as Post} />;
      pageUrl = data ? `${window.location.origin}/posts/${data.slug}` : '';
      entityName = data?.title || '';
      entityTypeName = 'Post';
      break;
    case 'event':
      content = <EventDrawerContent event={data} />;
      pageUrl = data ? `${window.location.origin}/events/${data.slug}` : '';
      entityName = data?.name || '';
      entityTypeName = 'Event';
      break;
    case 'artist':
      content = <ArtistDrawerContent artist={data} />;
      pageUrl = data ? `${window.location.origin}/artists/${data.slug}` : '';
      entityName = data?.name || '';
      entityTypeName = 'Artist';
      break;
    default:
      break;
  }

  // Custom header with buttons
  const drawerHeader = (
    <Group justify="space-between" style={{ width: '100%' }} color="gray">
      {/* No title here, just buttons */}
      <Group gap="md" color="gray">
        {/* Copy Link Button */}
        <CopyButton value={pageUrl} timeout={2000}>
          {({ copied, copy }) => (
            <Button
              size="sm"
              variant="filled"
              color="#ea580c"
              leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              style={{
                color: '#f2f2f2',
              }}
              onClick={copy}
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          )}
        </CopyButton>

        {/* Navigate to Page Button */}
        <Button
          component="a"
          href={pageUrl}
          target="_blank"
          size="sm"
          variant="filled"
          color="#ea580c"
          rightSection={<IconExternalLink size={16} />}
          style={{
            color: '#f2f2f2',
          }}
        >
          {entityTypeName} Page
        </Button>
      </Group>
    </Group>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      padding="md"
      size="lg"
      position="right"
      withCloseButton={true}
      title={drawerHeader}
      styles={{
        root: { zIndex: 1000 },
        header: {
          backgroundColor: '#18181B',
          color: '#f2f2f2',
          borderBottom: '1px solid #333',
          padding: '8px 16px',
        },
        body: {
          backgroundColor: '#18181B',
          color: '#f2f2f2',
          paddingTop: '20px',
        },
        title: {
          width: '100%',
          margin: 0,
          padding: 0,
        },
        close: {
          color: '#f2f2f2',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
        },
      }}
    >
      <div className="drawer-content" style={{ color: '#f2f2f2', padding: '0 16px' }}>
        {content}
      </div>
    </Drawer>
  );
}
